const ACCESS_TOKEN_KEY = "accessToken";
const AUTH_USER_KEY = "authUser";
const LOGIN_PATH = "/api/token/";
const UI_SETTINGS_PATH = "/api/ui_settings/";
const LOGIN_CODE = "string";

const trimTrailingSlash = (value = "") => value.replace(/\/+$/, "");

const getCookie = (name) => {
  if (typeof document === "undefined") return "";

  const value = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`));

  return value ? decodeURIComponent(value.split("=").slice(1).join("=")) : "";
};

const parseResponseBody = async (response) => {
  const rawText = await response.text();

  try {
    return {
      rawText,
      data: rawText ? JSON.parse(rawText) : null,
    };
  } catch {
    return {
      rawText,
      data: null,
    };
  }
};

export const getLoginUrl = () => {
  const envLoginUrl = process.env.REACT_APP_LOGIN_URL?.trim();
  if (envLoginUrl) {
    return envLoginUrl.endsWith(LOGIN_PATH)
      ? envLoginUrl
      : `${trimTrailingSlash(envLoginUrl)}${LOGIN_PATH}`;
  }

  return LOGIN_PATH;
};

export const getUiSettingsUrl = () => {
  const envLoginUrl = process.env.REACT_APP_LOGIN_URL?.trim();
  if (envLoginUrl) {
    return envLoginUrl.endsWith(UI_SETTINGS_PATH)
      ? envLoginUrl
      : `${trimTrailingSlash(envLoginUrl)}${UI_SETTINGS_PATH}`;
  }

  return UI_SETTINGS_PATH;
};

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

export const getStoredAuthUser = () => {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const setAccessToken = (token) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
};

export const setStoredAuthUser = (user) => {
  if (!user) return;
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
};

export const clearAccessToken = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
};

export const clearStoredAuthUser = () => {
  localStorage.removeItem(AUTH_USER_KEY);
};

export const isAuthenticated = () => Boolean(getAccessToken());

export const fetchUiSettings = async (token = getAccessToken()) => {
  if (!token) return null;

  const response = await fetch(getUiSettingsUrl(), {
    method: "GET",
    headers: {
      accept: "application/json",
      Authorization: `Token ${token}`,
    },
  });

  const { rawText, data } = await parseResponseBody(response);

  if (!response.ok) {
    const error = new Error(
      data?.detail || data?.message || rawText || "Failed to load ui settings",
    );
    error.status = response.status;
    throw error;
  }

  if (data?.user) {
    setStoredAuthUser(data.user);
  }

  return data;
};

export const loginWithPassword = async ({ username, password }) => {
  const csrfToken = getCookie("csrftoken") || getCookie("csrfToken");
  const headers = {
    accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded",
  };

  if (csrfToken) {
    headers["X-CSRFTOKEN"] = csrfToken;
  }

  const response = await fetch(getLoginUrl(), {
    method: "POST",
    headers,
    body: new URLSearchParams({
      username,
      password,
      code: LOGIN_CODE,
    }).toString(),
  });

  const { rawText, data } = await parseResponseBody(response);

  if (!response.ok || !data?.token) {
    const error = new Error(
      data?.detail || data?.message || rawText || "Login failed",
    );
    error.status = response.status;
    throw error;
  }

  setAccessToken(data.token);
  await fetchUiSettings(data.token).catch(() => null);
  return data.token;
};

export const logout = () => {
  clearAccessToken();
  clearStoredAuthUser();
  localStorage.removeItem("scan.sid");
  Object.keys(localStorage)
    .filter((key) => key.startsWith("scan.imageUrls."))
    .forEach((key) => localStorage.removeItem(key));
};
